import { fontSmoothing, getFontFamily } from '../../typography';
import idsTokensDefault from '../../utils/tokens/idsTokens';

const enabledStyles = (idsTokens = idsTokensDefault) => ({
  /** Input Field */
  '& .MuiAutocomplete-inputRoot': {
    color: idsTokens.textField.default.color.input.enabled,

    backgroundColor: idsTokens.textField.default.color.background.enabled,

    minWidth: idsTokens.textField.default.spacing.standard.min.width,
    minHeight: idsTokens.textField.default.spacing.standard.min.height,

    paddingTop: idsTokens.textField.default.spacing.standard.padding.top,
    paddingLeft: idsTokens.textField.default.spacing.standard.padding.left,
    paddingRight: `calc(${idsTokens.textField.default.spacing.standard.padding.right} + 32px)`,
    paddingBottom: idsTokens.textField.default.spacing.standard.padding.bottom,
    fontFamily: getFontFamily(idsTokens),
    ...fontSmoothing,
    fontSize: idsTokens.typography.font.size.base,
    lineHeight: idsTokens.typography.lineHeight.base,
    letterSpacing: idsTokens.typography.spacing.letter.lg,
    fontWeight: idsTokens.typography.font.weight.regular,
    fontStyle: 'normal',
  },
  '& .MuiAutocomplete-inputRoot.MuiInputBase-sizeSmall': {
    minWidth: idsTokens.textField.default.spacing.small.min.width,
    minHeight: idsTokens.textField.default.spacing.small.min.height,

    paddingTop: idsTokens.textField.default.spacing.small.padding.top,
    paddingLeft: idsTokens.textField.default.spacing.small.padding.left,
    paddingRight: `calc(${idsTokens.textField.default.spacing.small.padding.right} + 32px)`,
    paddingBottom: idsTokens.textField.default.spacing.small.padding.bottom,

    fontSize: idsTokens.typography.font.size.sm,
    lineHeight: idsTokens.typography.lineHeight.sm,
    letterSpacing: idsTokens.typography.spacing.letter.md,
    fontWeight: idsTokens.typography.font.weight.regular,
  },

  /** This is the <fieldset /> element that makes the outline around the input */
  '& .MuiOutlinedInput-notchedOutline': {
    borderRadius: idsTokens.textField.default.radius.standard,
    border: `1px solid ${idsTokens.textField.default.color.border.enabled}`,
  },
  '& .Mui-error .MuiOutlinedInput-notchedOutline': {
    borderRadius: idsTokens.textField.error.radius.standard,
    border: `2px solid ${idsTokens.textField.error.color.border.enabled}`,
  },

  /** Placeholder */
  '& .MuiInputBase-input::placeholder': {
    color: idsTokens.textField.default.color.placeholder,
    fontFamily: getFontFamily(idsTokens),
    ...fontSmoothing,
  },

  //placeholder when in error state
  '& .Mui-error .MuiInputBase-input::placeholder': {
    color: idsTokens.textField.error.color.placeholder,
    fontFamily: getFontFamily(idsTokens),
    ...fontSmoothing,
  },

  //input text selected valyue
  '& .MuiInputBase-input': {
    fontFamily: getFontFamily(idsTokens),
    ...fontSmoothing,
  },

  '& .MuiFormHelperText-root': {
    margin: 0,
    letterSpacing: idsTokens.typography.spacing.letter.md,
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    gap: 4,
    alignSelf: 'stretch',
    lineHeight: idsTokens.typography.lineHeight.sm,
  },
});

const hoverStyles = (idsTokens = idsTokensDefault) => ({
  '.IDS-Autocomplete-root:hover & .MuiAutocomplete-inputRoot': {
    color: idsTokens.textField.default.color.input.hover,
    backgroundColor: idsTokens.textField.default.color.background.hover,
  },
  /** This is the <fieldset /> element that makes the outline around the input */
  '.IDS-Autocomplete-root:hover & .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${idsTokens.textField.default.color.border.hover}`,
  },
  '.IDS-Autocomplete-root:hover & .Mui-error .MuiOutlinedInput-notchedOutline': {
    border: `2px solid ${idsTokens.textField.error.color.border.hover}`,
  },

  '.IDS-Autocomplete-root:hover & .MuiFormHelperText-root': {
    color: idsTokens.textField.default.color.help.hover,
    margin: 0,
    letterSpacing: idsTokens.typography.spacing.letter.md,
  },
  '.IDS-Autocomplete-root:hover & .MuiFormHelperText-root.Mui-error': {
    color: idsTokens.textField.error.color.help.hover,
  },
});

const focusedStyles = (idsTokens = idsTokensDefault) => ({
  '&.Mui-focused fieldset.MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${idsTokens.textField.default.color.border.focus}`,
    outline: `${idsTokens.functional.borderWidth.focus.default} solid ${idsTokens.functional.color.border.focus}`,
    outlineOffset: 2,
  },
  '&.Mui-focused .Mui-error .MuiOutlinedInput-notchedOutline': {
    border: `2px solid ${idsTokens.textField.error.color.border.focus}`,
  },
});

const pressedStyles = (idsTokens = idsTokensDefault) => ({
  '&:has(.MuiInputBase-input:active) .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${idsTokens.textField.default.color.border.pressed}`,
  },
  '&:has(.MuiInputBase-input[aria-invalid="true"]:active) .MuiOutlinedInput-notchedOutline': {
    border: `2px solid ${idsTokens.textField.error.color.border.pressed}`,
  },
});

const disabledStyles = (idsTokens = idsTokensDefault) => ({
  '& .MuiAutocomplete-inputRoot.Mui-disabled': {
    color: idsTokens.textField.default.color.input.disabled,
    backgroundColor: idsTokens.textField.default.color.background.disabled,
  },

  '& .MuiInputBase-input.Mui-disabled::placeholder': {
    color: idsTokens.textField.default.color.input.disabled,
  },

  '.IDS-Autocomplete-root & .Mui-disabled .MuiOutlinedInput-notchedOutline': {
    border: `1px solid ${idsTokens.textField.default.color.border.disabled}`,
  },

  '.IDS-Autocomplete-root & .MuiAutocomplete-inputRoot.Mui-disabled': {
    backgroundColor: idsTokens.textField.default.color.background.disabled,
  },

  '.IDS-Autocomplete-root & .MuiFormHelperText-root.Mui-disabled': {
    color: idsTokens.textField.default.color.help.disabled,
  },
});

export const classes = (idsTokens = idsTokensDefault) => ({
  root: {
    /**
     * these styles make the outline have the same proportions as the input
     * otherwise the outline becomes taller than the input field and the
     * focused outline looks off
     */
    '& fieldset.MuiOutlinedInput-notchedOutline': {
      top: 0,
      '& legend': {
        height: 0,
      },
    },
    ...enabledStyles(idsTokens),
    ...hoverStyles(idsTokens),
    ...focusedStyles(idsTokens),
    ...pressedStyles(idsTokens),
    ...disabledStyles(idsTokens),
  },
});
